import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Language } from '../types';

interface LanguageContextType {
    lang: Language;
    setLang: (lang: Language) => void;
    toggleLang: () => void;
    t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

const translations: Record<Language, Record<string, string>> = {
    zh: {
        // Hero
        'hero.title1': '更安全的合同',
        'action.tryNow': '立刻体验',
        'hero.title2': '更快的决策',
        'hero.subtitle': '人工智能驱动的文档审查流程，\n助力现代法律与商业团队。',
        'hero.getStarted': '立即开始',
        'hero.switch': 'Switch to English',

        // Features Loop
        'feature.title': 'AI 合同分析，',
        'feature.titleAccent': '发现隐藏风险',
        'feature.desc': 'DocSure AI 是一个专业的合同分析平台。通过先进的自然语言处理技术，我们科学地分析您的合同条款，提供客观的任务风险评估和个性化的修订建议。',
        'feature.item1.title': '全面风险扫描:',
        'feature.item1.desc': '深入分析合同条款，包括责任条款、终止条件和赔偿义务。',
        'feature.item2.title': '科学评分系统:',
        'feature.item2.desc': '基于庞大的法律数据库，提供客观准确的安全评分和详细的分析报告。',
        'feature.item3.title': '个性化建议:',
        'feature.item3.desc': '根据您的公司政策，提供具体的修订建议和优化方向。',
        'feature.item4.title': '专业指导:',
        'feature.item4.desc': '从风险识别到谈判策略，全面提升您的合同安全性。',
        'feature.tagline': 'DocSure AI — 用技术发现并放大您的谈判力。',

        // UI Card
        'ui.safetyScore': '安全评分',
        'ui.scoreDesc': '综合多维度评估，量化合同安全指数。',
        'ui.liability': '责任分析',
        'ui.liabilityStatus': '当前状态: 平衡 / 标准',
        'ui.compliance': '合规见解',
        'ui.complianceDesc': '风险类型: GDPR, 本地管辖',
        'ui.confidence': '置信度评分',
        'ui.financial': '财务条款',
        'ui.clarity': '条款清晰度',

        // Core Stack
        'stack.title': 'DocSure AI 核心功能栈',
        'stack.item1.title': '条款级风险分析',
        'stack.item1.subtitle': '(Clause-level Risk Analysis)',
        'stack.item1.desc': '智能识别潜在风险，提升合规效率。',
        'stack.item2.title': 'Reveal Path™ 流程视图',
        'stack.item2.subtitle': '(Process Visualization)',
        'stack.item2.desc': '可视化审批路径，追踪决策全流程。',
        'stack.item3.title': '导出已标注文档',
        'stack.item3.subtitle': '(Export Annotated Docs)',
        'stack.item3.desc': '一键导出带注释文件，方便共享与存档。',

        // FAQ
        'faq.title': '常见问题',
        'faq.contact': '还有疑问？通过邮件或 Discord 联系我们。',
        'faq.q1': '什么是 DocSure AI 合同分析？',
        'faq.a1': 'DocSure AI 是一个先进的平台，利用人工智能来审查、分析和改进您的法律合同。它就像一个 24/7 的法律助手。',
        'faq.q2': '“安全评分”是如何计算的？',
        'faq.a2': '我们的评分算法针对多个维度：合规性、公平性、清晰度和风险敞口进行评估，这些都是在数以千计的标准商业协议上训练而成的。',
        'faq.q3': '如何提高我的合同得分？',
        'faq.a3': '遵循分析报告中提供的个性化建议。接受“低风险”的修订通常会立即提高您的分值。',
        'faq.q4': 'DocSure AI 适用于所有合同吗？',
        'faq.a4': '是的。无论您是审查 NDA 的自由职业者，还是检查主服务协议的企业，DocSure 都能适应各种合同类型。',
        'faq.q5': '我的数据在 DocSure 安全吗？',
        'faq.a5': '我们优先考虑隐私。所有分析都在安全的环境中进行。未经许可，我们不会使用您的私有合同来训练我们的公共模型。',
        'faq.q6': '如何开始？',
        'faq.a6': '只需点击首页的“立即开始”按钮，上传您的文档 (docx)，即可立即获得全面的分析。',

        // Marquee
        'mq.label.gain': '本月预计收益',
        'mq.label.cardNumber': '账号信息',
        'mq.label.expires': '有效期限',
        'mq.label.cvc': '验证码',
        'mq.label.pay': '立即订阅',
        'mq.label.uploadMore': '继续上传',
        'mq.label.continue': '立即开始',
        'mq.label.reviewNow': '立即审查',
        'mq.engine': '自研引擎',
        'mq.engineDesc': '基于大模型微调，精准捕捉每一个细微风险。',
        'mq.instant': '瞬时响应',
        'mq.instantDesc': '毫秒级识别，彻底告别等待。',

        // App Layout / Sidebar
        'nav.workbench': '工作台',
        'nav.reveal': 'Reveal Path™',
        'nav.newProject': '新建项目',
        'nav.history': '历史记录',
        'nav.settings': '设置',
        'nav.back': '返回首页',
        'nav.logout': '退出登录',
        'nav.signOut': '退出登录',
        'nav.login': '登录',
        'nav.download': '下载',
        'nav.managePolicies': '管理规则',
        'nav.items': '项',
        'nav.noRecords': '暂无记录',

        // Workbench
        'wb.upload': '上传合同开始分析',
        'wb.support': '支持DOCX',
        'wb.or': '或',
        'wb.drag': '拖拽文件至此',
        'wb.recent': '最近审查',
        'wb.noHistory': '暂无历史记录',
        'wb.loading': '正在加载文档内容...',
        'wb.error': '文档文件不可用',
        'wb.errorDesc': '原始文档文件无法加载，可能已被删除或存储路径不正确。',

        // Config Panel
        'config.title': '智能审查配置',
        'config.position': '立场选择',
        'config.partyA': '甲方',
        'config.partyADesc': '合同发起方',
        'config.partyB': '乙方',
        'config.partyBDesc': '合同接收方',
        'config.intensity': '审查力度',
        'config.aggressive': '强势',
        'config.neutral': '中立',
        'config.defensive': '弱势',
        'config.aggressiveTip': '严格审查，最大化保护己方利益',
        'config.neutralTip': '平衡双方利益，关注关键风险',
        'config.defensiveTip': '灵活接受，仅关注重大风险',
        'config.optional': '可选',
        'config.analyzing': '分析中...',
        'config.start': '开始智能审查',
        'config.policy': '增强指引',
        'config.policy.none': '无特殊政策',
        'config.policy.loading': '加载政策中...',

        // Policy
        'policy.addNew': '新增规则',
        'policy.ruleName': '规则名称',
        'policy.ruleContent': '规则内容',
        'policy.save': '保存',
        'policy.cancel': '取消',
        'policy.delete': '删除',
        'policy.edit': '编辑',
        'policy.noPolicies': '暂无定制化审查规则',
        'policy.addFirst': '暂无规则，请在左侧侧边栏「个人中心」中添加',
        'policy.enableTip': '开启后应用您的专属审查规则',
        'policy.select': '请选择规则',

        // Risk Panel
        'risk.title': '条款风险详情',
        'risk.summary': '风险摘要',
        'risk.empty': '请在文档中选择标记条款以查看详细分析',
        'risk.analyzing': '分析中',
        'risk.level.critical': '极高风险',
        'risk.level.high': '高风险',
        'risk.level.medium': '中风险',
        'risk.level.low': '低风险',
        'risk.critical': '极高风险',
        'risk.high': '高风险',
        'risk.medium': '中风险',
        'risk.low': '低风险',
        'risk.criticalRisks': '极高风险',
        'risk.highRisks': '高风险',
        'risk.mediumRisks': '中风险',
        'risk.lowRisks': '低风险',
        'risk.analysis': '风险分析',
        'risk.suggestion': '修订建议',
        'risk.aiSuggestions': '风险建议',
        'risk.references': '法律参考',
        'risk.download': '下载批注版文档',
        'risk.dataAvailable': '✓ 风险分析数据可用',
        'risk.viewInPanel': '您可以在右侧面板查看分析结果',
        'risk.allRisks': '全部',
        'risk.clause': '条款',
        'risk.suggestedActionTitle': '修订建议：',

        // Reveal Path
        'reveal.title': 'Reveal Path™',
        'reveal.deepAnalysis': '深度分析',
        'reveal.scanning': '正在扫描',
        'reveal.thoughtChain': '思维链',
        'reveal.legalReferences': '相关法律条款',
        'reveal.viewDetails': '查看详情',
        'reveal.chatTitle': 'AI 答疑',
        'reveal.chatPlaceholder': '输入您的问题...',
        'reveal.chatSend': '发送',
        'reveal.chatLoading': '思考中...',
        'reveal.suggestedQuestion': '这份合同涉及到哪些法律法规',
        'reveal.presetQ1': '该合同涉及哪些法律法规？',
        'reveal.presetQ2': '合同中有哪些高风险条款？',
        'reveal.presetQ3': '如何优化责任与赔偿条款？',
        'reveal.presetQ4': '终止条件有哪些潜在风险？',
        'reveal.stepParsing': '正在解析文档…',
        'reveal.stepClauses': '正在识别条款…',
        'reveal.stepContext': '正在构建分析上下文…',
        'reveal.stepReady': '即将开始深度分析…',

    },
    en: {
        // Hero
        'hero.title1': 'Safer contracts.',
        'hero.title2': 'Faster decisions.',
        'hero.subtitle': 'An AI-driven document review pipeline \nfor modern legal and business teams.',
        'hero.getStarted': 'Get Started',
        'action.tryNow': 'Try Now',
        'hero.switch': '切换到中文',

        // Features Loop
        'feature.title': 'AI Contract Analysis, ',
        'feature.titleAccent': 'Discover Hidden Risks',
        'feature.desc': 'DocSure AI is a professional contract analysis platform. Through advanced natural language processing, we scientifically analyze your contract clauses, providing objective risk assessments and personalized revision suggestions.',
        'feature.item1.title': 'Comprehensive Risk Scan:',
        'feature.item1.desc': 'Deeply analyze contract terms, including liability clauses, termination conditions, and indemnity obligations.',
        'feature.item2.title': 'Scientific Scoring System:',
        'feature.item2.desc': 'Based on vast legal datasets, providing objective and accurate safety scores and detailed analysis reports.',
        'feature.item3.title': 'Personalized Suggestions:',
        'feature.item3.desc': 'Tailored to your company policy, offering specific revision suggestions and optimization directions.',
        'feature.item4.title': 'Professional Guidance:',
        'feature.item4.desc': 'From risk identification to negotiation strategy, comprehensively enhancing your contract security.',
        'feature.tagline': 'DocSure AI — Use technology to discover and amplify your negotiation power.',

        // UI Card
        'ui.safetyScore': 'Safety Score',
        'ui.scoreDesc': 'Comprehensive evaluation based on multiple dimensions',
        'ui.liability': 'Liability Analysis',
        'ui.liabilityStatus': 'Current Status: Balanced / Standard',
        'ui.compliance': 'Compliance Insights',
        'ui.complianceDesc': 'Risk Type: GDPR, Local Jurisdiction',
        'ui.confidence': 'Confidence Score',
        'ui.financial': 'Financial Terms',
        'ui.clarity': 'Clause Clarity',

        // Core Stack
        'stack.title': 'DocSure AI Core Feature Stack',
        'stack.item1.title': 'Clause-level Risk Analysis',
        'stack.item1.subtitle': '',
        'stack.item1.desc': 'Intelligently identify potential risks for improved compliance efficiency.',
        'stack.item2.title': 'Reveal Path™ Visualization',
        'stack.item2.subtitle': '',
        'stack.item2.desc': 'Visualize approval pathways for full decision tracking.',
        'stack.item3.title': 'Export Annotated Docs',
        'stack.item3.subtitle': '',
        'stack.item3.desc': 'One-click export with annotations for easy sharing and archiving.',

        // FAQ
        'faq.title': 'FAQ',
        'faq.contact': 'Still have questions? Contact us via email or Discord.',
        'faq.q1': 'What is DocSure AI Contract Analysis?',
        'faq.a1': 'DocSure AI is an advanced platform that uses Artificial Intelligence to review, analyze, and improve your legal contracts. It acts as a 24/7 legal assistant.',
        'faq.q2': 'How is the \'Safety Score\' calculated?',
        'faq.a2': 'Our scoring algorithm evaluates multiple dimensions: compliance, fairness, clarity, and risk exposure, trained on thousands of standard commercial agreements.',
        'faq.q3': 'How can I improve my contract score?',
        'faq.a3': 'Follow the personalized suggestions provided in the analysis report. Accepting the \'Low Risk\' revisions usually improves your score immediately.',
        'faq.q4': 'Is DocSure AI suitable for all contracts?',
        'faq.a4': 'Yes, whether you are a freelancer reviewing an NDA or a corporation checking a Master Service Agreement, DocSure adapts to various contract types.',
        'faq.q5': 'Is my data safe with DocSure?',
        'faq.a5': 'We prioritize privacy. All analyses are conducted in a secure environment. We do not use your private contracts to train our public models without permission.',
        'faq.q6': 'How do I get started?',
        'faq.a6': 'Simply click the \'Get Started\' button on the homepage, upload your document (docx), and receive your comprehensive analysis instantly.',

        // Marquee
        'mq.label.gain': 'Estimated Gain This Month',
        'mq.label.cardNumber': 'Card Number',
        'mq.label.expires': 'Expires',
        'mq.label.cvc': 'CVC',
        'mq.label.pay': 'Pay',
        'mq.label.uploadMore': 'Upload More',
        'mq.label.continue': 'Continue',
        'mq.label.reviewNow': 'Review Now',
        'mq.engine': 'Proprietary Engine',
        'mq.engineDesc': 'Based on fine-tuned LLMs to capture every subtle risk.',
        'mq.instant': 'Instant Response',
        'mq.instantDesc': 'Millisecond recognition, no more waiting.',

        // App Layout / Sidebar
        'nav.workbench': 'Workbench',
        'nav.reveal': 'Reveal Path™',
        'nav.newProject': 'New Project',
        'nav.history': 'History',
        'nav.settings': 'Settings',
        'nav.back': 'Back Home',
        'nav.logout': 'Sign Out',
        'nav.signOut': 'Sign Out',
        'nav.login': 'Sign In',
        'nav.download': 'Download',
        'nav.managePolicies': 'Manage Policies',
        'nav.items': 'items',
        'nav.noRecords': 'No records',

        // Workbench
        'wb.upload': 'Upload Contract to start analysis',
        'wb.support': 'Support DOCX',
        'wb.or': 'OR',
        'wb.drag': 'Drag files here',
        'wb.recent': 'Recent Reviews',
        'wb.noHistory': 'No history found',
        'wb.loading': 'Loading document content...',
        'wb.error': 'Document file not available',
        'wb.errorDesc': 'The original document file cannot be loaded. It may have been deleted or the storage path is incorrect.',

        // Config Panel
        'config.title': 'Smart Review Config',
        'config.position': 'Your Position',
        'config.partyA': 'Party A',
        'config.partyADesc': 'Contract initiator',
        'config.partyB': 'Party B',
        'config.partyBDesc': 'Contract recipient',
        'config.intensity': 'Review Intensity',
        'config.aggressive': 'Strong',
        'config.neutral': 'Neutral',
        'config.defensive': 'Flexible',
        'config.aggressiveTip': 'Strict review, maximize protection',
        'config.neutralTip': 'Balance both parties, focus on key risks',
        'config.defensiveTip': 'Flexible, only flag major risks',
        'config.optional': 'Optional',
        'config.analyzing': 'Analyzing...',
        'config.start': 'Start Smart Review',
        'config.policy': 'Enhanced Policy',
        'config.policy.none': 'No specific policy',
        'config.policy.loading': 'Loading policies...',

        // Policy
        'policy.addNew': 'Add Rule',
        'policy.ruleName': 'Rule Name',
        'policy.ruleContent': 'Rule Content',
        'policy.save': 'Save',
        'policy.cancel': 'Cancel',
        'policy.delete': 'Delete',
        'policy.edit': 'Edit',
        'policy.noPolicies': 'No custom review rules',
        'policy.addFirst': 'No rules, please add in "Personal Center" on the sidebar',
        'policy.enableTip': 'Enable to apply your custom rules',
        'policy.select': 'Select Policy',

        // Risk Panel
        'risk.title': 'Clause Risk Details',
        'risk.summary': 'Risk Summary',
        'risk.empty': 'Select flagged clauses in the document to view detailed analysis',
        'risk.analyzing': 'Analyzing',
        'risk.level.critical': 'Critical Risk',
        'risk.level.high': 'High Risk',
        'risk.level.medium': 'Medium Risk',
        'risk.level.low': 'Low Risk',
        'risk.critical': 'Critical',
        'risk.high': 'High',
        'risk.medium': 'Medium',
        'risk.low': 'Low',
        'risk.criticalRisks': 'Critical',
        'risk.highRisks': 'High',
        'risk.mediumRisks': 'Medium',
        'risk.lowRisks': 'Low',
        'risk.analysis': 'Risk Analysis',
        'risk.suggestion': 'Suggested Action',
        'risk.aiSuggestions': 'Risk Suggestions',
        'risk.references': 'Legal References',
        'risk.download': 'Download Annotated Doc',
        'risk.dataAvailable': '✓ Risk analysis data available',
        'risk.viewInPanel': 'You can view analysis results in the right panel',
        'risk.allRisks': 'All',
        'risk.clause': 'Clause',
        'risk.suggestedActionTitle': 'Suggested Action:',

        // Reveal Path
        'reveal.title': 'Reveal Path™',
        'reveal.deepAnalysis': 'Deep Analysis',
        'reveal.scanning': 'Scanning',
        'reveal.thoughtChain': 'Thought Chain',
        'reveal.legalReferences': 'Related Legal References',
        'reveal.viewDetails': 'View Details',
        'reveal.chatTitle': 'AI Q&A',
        'reveal.chatPlaceholder': 'Type your question...',
        'reveal.chatSend': 'Send',
        'reveal.chatLoading': 'Thinking...',
        'reveal.suggestedQuestion': 'What laws and regulations does this contract involve?',
        'reveal.presetQ1': 'What laws and regulations does this contract involve?',
        'reveal.presetQ2': 'What high-risk clauses are in this contract?',
        'reveal.presetQ3': 'How to optimize liability and indemnity clauses?',
        'reveal.presetQ4': 'What potential risks are in the termination conditions?',
        'reveal.stepParsing': 'Parsing document…',
        'reveal.stepClauses': 'Identifying clauses…',
        'reveal.stepContext': 'Building analysis context…',
        'reveal.stepReady': 'Starting deep analysis…',

    }
};

interface LanguageProviderProps {
    children: ReactNode;
    initialLang?: Language;
    onLangChange?: (lang: Language) => void;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
    children,
    initialLang = 'zh',
    onLangChange
}) => {
    const [lang, setLangInternal] = useState<Language>(initialLang);

    const setLang = (newLang: Language) => {
        setLangInternal(newLang);
        if (onLangChange) onLangChange(newLang);
    };

    const toggleLang = () => {
        const newLang = lang === 'zh' ? 'en' : 'zh';
        setLang(newLang);
    };

    const t = (key: string) => {
        return translations[lang][key] || key;
    };

    return (
        <LanguageContext.Provider value={{ lang, setLang, toggleLang, t }}>
            {children}
        </LanguageContext.Provider>
    );
};
